// Panel table handler functions

import { filterNumericInput, validateNumericField, validatePanel } from './validationUtils';

/**
 * Handle panel field change
 * @param {number} idx - Panel index
 * @param {string} field - Field name
 * @param {string} value - New value
 * @param {Array} panels - Current panels array
 * @param {Function} setPanels - State setter for panels
 * @param {Object} invalidPanelFields - Current invalid fields object
 * @param {Function} setInvalidPanelFields - State setter for invalid fields
 * @param {boolean} differentMaterials - Whether different materials are enabled
 */
export const handlePanelChange = (idx, field, value, panels, setPanels, invalidPanelFields, setInvalidPanelFields, differentMaterials = false) => {
  // Filter input for numeric fields to only allow valid characters
  let filteredValue = value;
  if (['width', 'length', 'quantity'].includes(field)) {
    filteredValue = filterNumericInput(value, field);
  }
  
  // Update state with filtered value
  const updated = panels.map((row, i) => i === idx ? { ...row, [field]: filteredValue } : row);
  setPanels(updated);

  const fieldKey = `${idx}-${field}`;
  const currentPanel = panels[idx];

  // Handle validation for numeric fields
  if (['width', 'length', 'quantity'].includes(field)) {
    // Allow empty values during typing
    if (filteredValue === '' || filteredValue === null) {
      // Don't mark as invalid during typing - will validate on blur
      return;
    }
    
    // Use centralized validation function
    const validation = validateNumericField(filteredValue, field, currentPanel.enabled);
    
    // Update invalid fields tracking only for clearly invalid values
    setInvalidPanelFields(prev => {
      if (validation.isValid) {
        // Remove from invalid list
        const newInvalid = { ...prev };
        delete newInvalid[fieldKey];
        return newInvalid;
      } else {
        // Add to invalid list
        return { ...prev, [fieldKey]: true };
      }
    });
  } else if (field === 'materialType' && differentMaterials) {
    // Validate material type immediately when changed
    const isValid = filteredValue !== '' && filteredValue !== null && filteredValue !== undefined;
    setInvalidPanelFields(prev => {
      if (isValid) {
        const newInvalid = { ...prev };
        delete newInvalid[fieldKey];
        return newInvalid;
      } else {
        return { ...prev, [fieldKey]: true };
      }
    });
  }
};

/**
 * Handle panel field blur event
 * @param {number} idx - Panel index
 * @param {string} field - Field name
 * @param {Array} panels - Current panels array
 * @param {Object} invalidPanelFields - Current invalid fields object
 * @param {Function} setInvalidPanelFields - State setter for invalid fields
 * @param {boolean} differentMaterials - Whether different materials are enabled
 */
export const handlePanelBlur = (idx, field, panels, invalidPanelFields, setInvalidPanelFields, differentMaterials = false) => {
  // Validate on blur - if enabled and empty, mark as invalid
  const panel = panels[idx];
  const fieldKey = `${idx}-${field}`;
  
  if (['width', 'length', 'quantity'].includes(field) && panel.enabled) {
    const value = panel[field];
    
    // Use centralized validation function
    const validation = validateNumericField(value, field, panel.enabled);

    setInvalidPanelFields(prev => {
      if (validation.isValid) {
        const newInvalid = { ...prev };
        delete newInvalid[fieldKey];
        return newInvalid;
      } else {
        return { ...prev, [fieldKey]: true };
      }
    });
  } else if (field === 'materialType' && differentMaterials && panel.enabled) {
    // Validate material type on blur
    const value = panel.materialType;
    const isValid = value !== '' && value !== null && value !== undefined;
    
    setInvalidPanelFields(prev => {
      if (isValid) {
        const newInvalid = { ...prev };
        delete newInvalid[fieldKey];
        return newInvalid;
      } else {
        return { ...prev, [fieldKey]: true };
      }
    });
  }
};

/**
 * Toggle panel enabled state
 * @param {number} idx - Panel index
 * @param {Array} panels - Current panels array
 * @param {Function} setPanels - State setter for panels
 * @param {Object} invalidPanelFields - Current invalid fields object
 * @param {Function} setInvalidPanelFields - State setter for invalid fields
 * @param {boolean} differentMaterials - Whether different materials are enabled
 */
export const togglePanelEnabled = (idx, panels, setPanels, invalidPanelFields, setInvalidPanelFields, differentMaterials = false) => {
  const updated = panels.map((row, i) => i === idx ? { ...row, enabled: !row.enabled } : row);
  setPanels(updated);
  
  // Re-validate fields when enabled state changes
  const panel = updated[idx];
  if (panel.enabled) {
    // Panel is now enabled - validate all required fields using centralized validation
    const panelValidation = validatePanel(panel, idx, differentMaterials);
    const newInvalid = { ...invalidPanelFields };
    
    // Update invalid fields based on validation result
    panelValidation.invalidFields.forEach(fieldKey => {
      newInvalid[fieldKey] = true;
    });
    
    // Remove fields that are now valid
    const fieldsToCheck = differentMaterials ? ['width', 'length', 'quantity', 'materialType'] : ['width', 'length', 'quantity'];
    fieldsToCheck.forEach(field => {
      const fieldKey = `${idx}-${field}`;
      if (!panelValidation.invalidFields.includes(fieldKey)) {
        delete newInvalid[fieldKey];
      }
    });
    
    setInvalidPanelFields(newInvalid);
  } else {
    // Panel is now disabled - remove validation errors for this panel
    const newInvalid = { ...invalidPanelFields };
    const fieldsToCheck = differentMaterials ? ['width', 'length', 'quantity', 'materialType'] : ['width', 'length', 'quantity'];
    fieldsToCheck.forEach(field => {
      delete newInvalid[`${idx}-${field}`];
    });
    setInvalidPanelFields(newInvalid);
  }
};

/**
 * Toggle panel grain direction
 * @param {number} idx - Panel index
 * @param {Array} panels - Current panels array
 * @param {Function} setPanels - State setter for panels
 */
export const togglePanelGrain = (idx, panels, setPanels) => {
  const updated = panels.map((row, i) => {
    if (i === idx) {
      const grainCycle = { either: 'horizontal', horizontal: 'vertical', vertical: 'either' };
      return { ...row, grainDirection: grainCycle[row.grainDirection] };
    }
    return row;
  });
  setPanels(updated);
};

/**
 * Add a new panel
 * @param {Array} panels - Current panels array
 * @param {Function} setPanels - State setter for panels
 */
export const addPanel = (panels, setPanels) => {
  // Check if there's already an empty panel (width, length, and quantity are all empty)
  const hasEmptyPanel = panels.some(panel => 
    (!panel.width || panel.width === '') && 
    (!panel.length || panel.length === '') && 
    (!panel.quantity || panel.quantity === '')
  );
  
  // Only add a new panel if there's no empty panel already
  if (!hasEmptyPanel) {
    setPanels([...panels, { width: '', length: '', quantity: '', materialType: '', label: '', enabled: true, grainDirection: 'either' }]);
  }
};

/**
 * Remove a panel
 * @param {number} idx - Panel index
 * @param {Array} panels - Current panels array
 * @param {Function} setPanels - State setter for panels
 */
export const removePanel = (idx, panels, setPanels) => {
  setPanels(panels.filter((_, i) => i !== idx));
};

